<script setup>
import { computed, ref, watch } from 'vue';

const props = defineProps({
  orders: {
    type: Array,
    default: () => [],
  },
});

defineEmits(['close']);

const selectedOrderIndex = ref(0);
const selectedOrder = computed(() => props.orders[selectedOrderIndex.value]);

watch(
  () => props.orders,
  () => {
    selectedOrderIndex.value = 0;
  }
);

const statusLabels = {
  Pending: '待处理',
  Unshipped: '待发货',
  PartiallyShipped: '部分发货',
  Shipped: '已发货',
  InvoiceUnconfirmed: '发票待确认',
  Canceled: '已取消',
  Unfulfillable: '无法配送',
};

const syncLabels = {
  synced: '已同步',
  pending: '同步中',
  failed: '同步失败',
  not_found: '未找到',
};

const formatDate = value => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

const formatDateRange = (start, finish) => {
  if (!start && !finish) return '—';
  if (!start) return `至 ${formatDate(finish)}`;
  if (!finish) return `${formatDate(start)} 起`;
  return `${formatDate(start)} 至 ${formatDate(finish)}`;
};

const formatMoney = (amount, currency) => {
  if (amount === null || amount === undefined || amount === '') return '—';
  try {
    return new Intl.NumberFormat('zh-CN', {
      style: 'currency',
      currency: currency || 'USD',
      maximumFractionDigits: 2,
    }).format(Number(amount));
  } catch {
    return `${amount} ${currency || ''}`.trim();
  }
};
</script>

<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<template>
  <aside class="flex-col bg-n-surface-2">
    <header
      class="flex h-12 shrink-0 items-center justify-between border-b border-n-weak px-3"
    >
      <div class="flex items-center gap-2">
        <span class="text-sm font-medium text-n-slate-12">订单详情</span>
        <span
          class="rounded-md bg-n-iris-3 px-1.5 py-0.5 text-[10px] font-medium text-n-iris-11"
        >
          模拟
        </span>
      </div>
      <button
        type="button"
        class="flex size-8 items-center justify-center rounded-lg text-n-slate-10 hover:bg-n-alpha-2"
        title="关闭订单详情"
        @click="$emit('close')"
      >
        <span class="i-lucide-x size-4" aria-hidden="true" />
      </button>
    </header>

    <div class="flex-1 overflow-y-auto p-3">
      <div
        v-if="!orders.length"
        class="flex min-h-72 flex-col items-center justify-center px-6 text-center"
      >
        <span
          class="i-lucide-shopping-bag size-8 text-n-slate-9"
          aria-hidden="true"
        />
        <p class="mt-3 text-sm font-medium text-n-slate-12">暂无模拟订单</p>
      </div>

      <template v-else>
        <div
          v-if="orders.length > 1"
          class="mb-3 flex gap-1 overflow-x-auto rounded-lg bg-n-alpha-2 p-1"
        >
          <button
            v-for="(order, index) in orders"
            :key="order.amazon_order_id"
            type="button"
            class="shrink-0 rounded-md px-2 py-1.5 text-xs font-medium"
            :class="
              index === selectedOrderIndex
                ? 'bg-n-solid-2 text-n-slate-12 shadow-sm'
                : 'text-n-slate-10'
            "
            @click="selectedOrderIndex = index"
          >
            {{ order.amazon_order_id }}
          </button>
        </div>

        <section
          v-if="selectedOrder"
          class="overflow-hidden rounded-xl border border-n-weak bg-n-solid-2"
        >
          <div class="p-3">
            <div class="flex items-start justify-between gap-2">
              <p class="min-w-0 break-all text-sm font-semibold text-n-blue-11">
                {{ selectedOrder.amazon_order_id }}
              </p>
              <button
                type="button"
                class="flex shrink-0 items-center gap-1 text-xs text-n-slate-11"
              >
                操作
                <span
                  class="i-lucide-chevron-down size-3.5"
                  aria-hidden="true"
                />
              </button>
            </div>
            <div class="mt-2 flex flex-wrap gap-1.5">
              <span
                v-if="selectedOrder.order_status"
                class="rounded-md bg-n-amber-3 px-2 py-1 text-xs font-medium text-n-amber-11"
              >
                {{
                  statusLabels[selectedOrder.order_status] ||
                  selectedOrder.order_status
                }}
              </span>
              <span
                class="rounded-md bg-n-teal-3 px-2 py-1 text-xs font-medium text-n-teal-11"
              >
                {{
                  syncLabels[selectedOrder.sync_status] ||
                  selectedOrder.sync_status
                }}
              </span>
            </div>
            <div
              v-if="selectedOrder.sync_status !== 'synced'"
              class="mt-3 rounded-lg bg-n-alpha-2 px-3 py-2 text-xs leading-5 text-n-slate-11"
            >
              <p>
                {{ selectedOrder.sync_error_message || '模拟订单正在同步。' }}
              </p>
              <p v-if="selectedOrder.next_sync_at" class="mt-1 text-n-slate-9">
                下次尝试：{{ formatDate(selectedOrder.next_sync_at) }}
              </p>
            </div>
          </div>

          <template v-if="selectedOrder.sync_status === 'synced'">
            <dl class="grid gap-1.5 px-3 pb-3">
              <div
                v-for="detail in [
                  ['订购时间', formatDate(selectedOrder.purchase_date_utc)],
                  [
                    '发货时限',
                    formatDateRange(
                      selectedOrder.earliest_ship_at,
                      selectedOrder.latest_ship_at
                    ),
                  ],
                  ['发货时间', formatDate(selectedOrder.shipped_at)],
                  [
                    '收货预计',
                    formatDateRange(
                      selectedOrder.earliest_delivery_at,
                      selectedOrder.latest_delivery_at
                    ),
                  ],
                  [
                    '订购金额',
                    formatMoney(
                      selectedOrder.order_total_amount,
                      selectedOrder.currency
                    ),
                  ],
                  ['物流商', selectedOrder.shipping_service || '—'],
                  ['物流方式', selectedOrder.fulfillment_channel || '—'],
                ]"
                :key="detail[0]"
                class="grid grid-cols-[76px_minmax(0,1fr)] gap-2 text-xs leading-5"
              >
                <dt class="text-n-slate-9">{{ detail[0] }}</dt>
                <dd class="min-w-0 break-words font-medium text-n-slate-12">
                  {{ detail[1] }}
                </dd>
              </div>
            </dl>

            <div class="divide-y divide-n-weak border-t border-n-weak">
              <p
                v-if="!selectedOrder.items.length"
                class="px-3 py-8 text-center text-xs text-n-slate-9"
              >
                模拟订单没有商品明细
              </p>
              <template v-else>
                <article
                  v-for="item in selectedOrder.items"
                  :key="item.order_item_id"
                  class="flex flex-wrap items-start gap-3 px-3 py-3"
                >
                  <div
                    class="relative flex size-14 shrink-0 items-center justify-center rounded-lg border border-n-weak bg-n-alpha-1"
                  >
                    <img
                      v-if="item.product_image_url"
                      :src="item.product_image_url"
                      :alt="item.product_name || item.title || item.asin"
                      class="size-full rounded-lg object-contain"
                    />
                    <span
                      v-else
                      class="i-lucide-package size-6 text-n-slate-9"
                      aria-hidden="true"
                    />
                    <span
                      class="absolute bottom-0 left-0 rounded-tr bg-n-slate-12 px-1.5 py-0.5 text-[10px] font-medium text-white"
                    >
                      ×{{ item.quantity_ordered || 1 }}
                    </span>
                  </div>
                  <div class="min-w-0 flex-1">
                    <p class="truncate text-sm font-medium text-n-slate-12">
                      {{ item.product_name || item.title || item.asin }}
                    </p>
                    <p class="mt-1.5 text-xs text-n-slate-9">
                      ASIN
                      <span class="font-medium text-n-blue-11">{{
                        item.asin
                      }}</span>
                    </p>
                    <p class="mt-1 break-all text-xs text-n-slate-9">
                      MSKU
                      <span class="font-medium text-n-slate-12">
                        {{ item.seller_sku }}
                      </span>
                    </p>
                  </div>
                  <div
                    class="w-full rounded-lg bg-n-alpha-1 px-3 py-2 text-xs text-n-slate-10"
                  >
                    <p>本地 SKU：{{ item.local_sku || '—' }}</p>
                    <p class="mt-1">
                      商品金额：{{
                        formatMoney(
                          item.item_price_amount,
                          item.currency || selectedOrder.currency
                        )
                      }}
                    </p>
                  </div>
                </article>
              </template>
            </div>
          </template>
        </section>

        <p class="px-1 pt-2 text-center text-xs text-n-slate-9">
          最近同步：{{ formatDate(selectedOrder?.last_synced_at) }}
        </p>
      </template>
    </div>
  </aside>
</template>
